import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { DISCOUNT_CODE_PATH } from '../../../../config/paths';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import DiscountCodeForm from './DiscountCodeForm';
import {
  clearDiscountCode,
  getDiscountCode,
  putDiscountCode,
} from '../../../../actions/sales/DiscountCode';
import { tzNormalizeDate } from '../../../../utils/date';
import {
  CONTRACT_TYPE,
  DISCOUNT_CODE_FLOOR_RULE,
  DISCOUNT_CODE_TYPE,
} from '../../../../config/constants';

const EditDiscountCode = ({
  breadcrumbs,
  dispatchGetDiscountCode,
  dispatchClearDiscountCode,
  dispatchPutDiscountCode,
  match: {
    params: { id: discountCodeId },
  },
  loading,
  discountCode,
}) => {
  useLayoutEffect(() => {
    dispatchGetDiscountCode({ discountCodeId });

    return () => dispatchClearDiscountCode();
  }, []);

  const onSubmit = (formValues) => {
    const newFormValues = {
      ...formValues,
      validFrom: formValues.validFrom
        ? tzNormalizeDate({ date: formValues.validFrom })
        : null,
      validUntil: formValues.validUntil
        ? tzNormalizeDate({ date: formValues.validUntil })
        : null,

      includedRouteSet: formValues.includedRouteSet
        ? formValues.includedRouteSet.map((route) => ({ id: route.value }))
        : [],

      excludedRouteSet: formValues.excludedRouteSet
        ? formValues.excludedRouteSet.map((route) => ({ id: route.value }))
        : [],

      includeAgencySet: formValues.includeAgencySet
        ? formValues.includeAgencySet.map((user) => ({ id: user.value }))
        : [],

      includeUserSet: formValues.includeUserSet
        ? formValues.includeUserSet.map((user) => ({ id: user.value }))
        : [],

      customerSet: formValues.customerSet
        ? formValues.customerSet.map((customer) => ({ id: customer.value }))
        : [],

      includeSecurityProfileSet: formValues.includeSecurityProfileSet
        ? formValues.includeSecurityProfileSet.map((securityProfile) => ({
            id: securityProfile.value,
          }))
        : [],

      agencyContractTypeDiscountCodeSet:
        formValues.agencyContractTypeDiscountCodeSet
          ? formValues.agencyContractTypeDiscountCodeSet.map(
              (contractType) => ({
                contractType: contractType.value,
              }),
            )
          : [],

      excludedDateRangeSet: formValues.excludedDateRangeSet || [],

      serviceTypeId: formValues.serviceType
        ? formValues.serviceType.value
        : null,

      discountCodeTripRuleList: formValues.discountCodeTripRuleList
        ? formValues.discountCodeTripRuleList.map((discountCodeTripRule) => ({
            serviceTypeId: discountCodeTripRule.serviceTypeId.value,
            discountFloorRule: discountCodeTripRule.discountFloorRule.value,
          }))
        : [],
      discountType: formValues.discountType
        ? formValues.discountType.value
        : null,
      numberOfUses: discountCode.get('numberOfUses'),

      discountCodeCategoryId: formValues.discountCodeCategoryId
        ? formValues.discountCodeCategoryId.value
        : null,

      authorizerUserId: formValues.authorizerUserId
        ? formValues.authorizerUserId.value
        : null,

      productType: formValues.productType ? formValues.productType.value : null, 
      
    };

    dispatchPutDiscountCode(discountCodeId, newFormValues);
  };

  const generateInitialValues = (discountCodeJson) => ({
    name: discountCodeJson.name,
    description: discountCodeJson.description,
    code: discountCodeJson.code,
    discountType:
      discountCodeJson.discountType &&
      DISCOUNT_CODE_TYPE[discountCodeJson.discountType],
    discount: discountCodeJson.discount,
    maxAmount: discountCodeJson.maxAmount,
    maxNumberOfUses: discountCodeJson.maxNumberOfUses,
    serviceType: discountCodeJson.serviceType
      ? {
          value: discountCodeJson.serviceType.id,
          label: discountCodeJson.serviceType.name,
        }
      : null,
    isValid: discountCodeJson.isValid,
    applyOnlyAfterDeparture: discountCodeJson.applyOnlyAfterDeparture
      ? discountCodeJson.applyOnlyAfterDeparture
      : false,
    validFrom:
      discountCodeJson.validFrom &&
      tzNormalizeDate({ date: discountCodeJson.validFrom }),
    validUntil:
      discountCodeJson.validUntil &&
      tzNormalizeDate({ date: discountCodeJson.validUntil }),
    excludedDateRangeSet: discountCodeJson.excludedDateRangeSet.map(
      (dates) => ({
        dateFrom: tzNormalizeDate({ date: dates.dateFrom }),
        dateUntil: tzNormalizeDate({ date: dates.dateUntil }),
      }),
    ),
    discountCodeTripRuleList: discountCodeJson.discountCodeTripRuleList.map(
      (discountCodeTripRule) => ({
        serviceTypeId: {
          value: discountCodeTripRule
            ? discountCodeTripRule.serviceType.id
            : null,
          label: discountCodeTripRule
            ? discountCodeTripRule.serviceType.name
            : null,
        },
        discountFloorRule:
          DISCOUNT_CODE_FLOOR_RULE[discountCodeTripRule.discountFloorRule],
      }),
    ),
    includedRouteSet: discountCodeJson.includedRouteSet.map((route) => ({
      value: route.id,
      label: route.name,
    })),
    excludedRouteSet: discountCodeJson.excludedRouteSet.map((route) => ({
      value: route.id,
      label: route.name,
    })),
    includeAgencySet: discountCodeJson.includeAgencySet.map((agency) => ({
      value: agency.id,
      label: agency.name,
    })),

    includeUserSet: discountCodeJson.includeUserSet.map((user) => ({
      value: user.id,
      label: user.fullName,
    })),
    customerSet: discountCodeJson.customerSet.map((customer) => ({
      value: customer.id,
      label: customer.fullName,
    })),

    includeSecurityProfileSet: discountCodeJson.includeSecurityProfileSet.map(
      (securityProfile) => ({
        value: securityProfile.id,
        label: securityProfile.name,
      }),
    ),

    agencyContractTypeDiscountCodeSet:
      discountCodeJson.agencyContractTypeDiscountCodeSet.map(
        (contractType) => ({
          value: contractType.contractType,
          label: CONTRACT_TYPE[contractType.contractType].label,
        }),
      ),
    authorizerUserId: discountCodeJson.authorizerUser
      ? {
          value: discountCodeJson.authorizerUser.id,
          label: discountCodeJson.authorizerUser.fullName,
        }
      : null,
    discountCodeCategoryId: discountCodeJson.discountCodeCategory
      ? {
          value: discountCodeJson.discountCodeCategory.id,
          label: discountCodeJson.discountCodeCategory.name,
        }
      : null,
  });

  let content;

  if (loading) content = <Loader />;
  else if (discountCode.isEmpty())
    content = <NoDataResource returnPage={DISCOUNT_CODE_PATH} />;
  else
    content = (
      <DiscountCodeForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues(discountCode.toJS())}
      />
    );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Código de Descuento"
      subtitle="Editar código de descuento"
      content={content}
    />
  );
};

EditDiscountCode.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchClearDiscountCode: PropTypes.func.isRequired,
  dispatchGetDiscountCode: PropTypes.func.isRequired,
  dispatchPutDiscountCode: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  discountCode: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditDiscountCode.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchClearDiscountCode: clearDiscountCode,
  dispatchGetDiscountCode: getDiscountCode,
  dispatchPutDiscountCode: putDiscountCode,
};

const mapStateToProps = (
  { SalesUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...SalesUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Códigos de Descuento',
      href: DISCOUNT_CODE_PATH,
    },
    {
      text: 'Ver',
      href: `${DISCOUNT_CODE_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  discountCode: SalesUnit.DiscountCode.getIn(['current', 'content']),
  loading: !SalesUnit.DiscountCode.getIn(['current', 'activity']).isEmpty(),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDiscountCode);
